import { useCallback, useEffect, useMemo } from 'react'
import { useLocation, useParams } from 'react-router'
import { CategoryModelTypeExtended } from '../../ProductsParent/Categories/types'
import { ComponentWithPermissions } from '../../../../../../baseComponents/CreateRoutering/types'
import { permissionCheck } from '../../../../../../baseComponents/PermissionChecker/function'
import { TwinTableLink } from '../../../../../../baseComponents/TwinTable'
import BaseScreenTable, { BaseScreenTableSimple } from '../../../../../../baseComponents/TwinTable/BaseScreenTable'
import { TableComponentProps } from '../../../../../../baseComponents/TwinTable/BaseScreenTable/types'
import { CustomRenderString, EditRowNotLink, DeleteRow } from '../../../../../../baseComponents/TwinTable/VirtualTableListing/CustomRenders'
import { ColumnTableSchema } from '../../../../../../baseComponents/TwinTable/VirtualTableListing/Subcomponents/types'
import { twinFetchPostJSON } from '../../../../../../utils/globals/data'
import useTwinTranslation from '../../../../../../utils/hooks/useTwinTranslation'
import { setVariablesToCurrentPage } from '../../../../../../utils/reducers/reduxDispatch'
import { getBasePath } from '../../../../../../utils/globals/link'
import ModalCEAddonCategory from './ModalCEAddonCategory'
import ModalDeleteAddonCategory from './ModalDeleteAddonCategory'
import CategoriesSortButton from '../../../../../../specificComponents/Category/CategoriesSortButton'

interface AddonCategoriesProps extends ComponentWithPermissions { }

const AddonCategories: React.FC<AddonCategoriesProps> = ({ userPermissions }) => {
    const { t } = useTwinTranslation()
    const { CategoryId } = useParams()
    useCategoriesLogic()
    const pageData = {
        title: t('categoriesAddons', 'Categorías de complementos'),
        newText: t('newCategories', 'Nueva categoría')
    }
    if (!CategoryId) {
        return <BaseScreenTableSimple TableComponent={TableAddonCategories} EditModalComponent={ModalCEAddonCategory} DeleteModalComponent={ModalDeleteAddonCategory} pageData={pageData} userPermissions={userPermissions} />
    }

    return (
        <BaseScreenTable TableComponent={TableAddonCategories} CreateModalComponent={ModalCEAddonCategory} EditModalComponent={ModalCEAddonCategory} DeleteModalComponent={ModalDeleteAddonCategory} pageData={pageData} userPermissions={userPermissions} />
    )
}

interface TableAddonCategoriesProps extends TableComponentProps { }

const TableAddonCategories: React.FC<TableAddonCategoriesProps> = ({ userPermissions, setTableInstance, setOpened, tableInstance }) => {
    const { t } = useTwinTranslation()
    const { CategoryId } = useParams()
    const location = useLocation()
    const basePath = getBasePath(location.pathname, 'categoriesAddons')
    const extraWhereParams = { 'Category.CategoryId': CategoryId ? parseInt(CategoryId) : null, categoryMaterialType: CategoryId? 5: null }
    const columns: ColumnTableSchema<CategoryModelTypeExtended> = [
        {
            id: 'id',
            dataKey: 'id',
            label: t('id', 'ID'),
            sortable: true,
            width: 75
        },
        {
            id: 'name',
            dataKey: 'CategoryLangs.name',
            label: t('name', 'Nombre'),
            sortable: true,
            minWidth: 130,
            searchableComponent: {
                component: 'SearchableComponentBasic',
                extraComponentData: { label: t('name', 'Nombre') }
            },
            customRender: (parameterValue) => <CustomRenderString value={parameterValue !== null ? parameterValue : t('base', 'Base')} />
        },
        {
            id: 'ref_code',
            dataKey: 'ref_code',
            label: t('code', 'Código'),
        },
        {
            id: 'order',
            dataKey: 'order',
            label: t('order', 'Orden'),
        },
    ]
    if (CategoryId) {
        columns.push({
            id: 'edit',
            dataKey: 'id',
            label: '',
            width: 50,
            simpleLabel: t('edit', 'Editar'),
            customRender: (parameterValue, allRowData) => <EditRowNotLink key={'edit' + parameterValue} {...{ parameterValue, allRowData }} onClick={(_idk, allRowData) => setOpened({ type: 'edit', allRowData })} />
        })
        columns.push({
            id: 'delete',
            dataKey: 'id',
            label: '',
            width: 50,
            simpleLabel: t('delete', 'Eliminar'),
            customRender: (parameterValue, allRowData) => <DeleteRow key={'delete' + parameterValue} {...{ parameterValue, allRowData }} onClick={(_idk, allRowData) => setOpened({ type: 'delete', allRowData })} />,
            havePermission: permissionCheck(userPermissions, 'delete')
        })
    }
    const children = useMemo(() => {
        const myExtraWhereParams = { 'Category.CategoryId': CategoryId ? parseInt(CategoryId) : null, categoryMaterialType: CategoryId ? 5 : null }
        return <CategoriesSortButton tableInstance={tableInstance} userPermissions={userPermissions} extraWhereParams={myExtraWhereParams} />
    }, [tableInstance, userPermissions, CategoryId])
    
    return (
        <TwinTableLink createRowDataLink={[basePath, ['$id']]} name='addonCategories' columns={columns} extraWhereParams={extraWhereParams} getDataFrom='/api/app/product/category/getAllCategories' setTableInstance={setTableInstance} defaultOrder={{ defaultOrderField: 'order', order: 'asc' }} >
            {children}
        </TwinTableLink>
    )
}
const useCategoriesLogic = () => {
    const { CategoryId } = useParams()
    const {t} = useTwinTranslation()
    const baseStr = t('base', 'Base')
    const getData = useCallback(async () => {
        const categoryName = await twinFetchPostJSON('/api/app/product/category/getCategoryName', {
            id: CategoryId,
        })
        if (categoryName) {
            setVariablesToCurrentPage([categoryName['name'] || baseStr])
        }
    }, [baseStr, CategoryId])

    useEffect(() => {
        if (CategoryId) {
            getData()
        }
    }, [CategoryId, getData])
}

export default AddonCategories